/**
 * Design Token System — Types & Defaults
 * 
 * All colors are stored as HSL strings (e.g. "184 65% 34%")
 * to be compatible with CSS variables and Tailwind.
 */

export interface DesignTokenSet {
  // App backgrounds
  background: string;
  foreground: string;
  
  // Card / surface
  card: string;
  cardForeground: string;
  
  // Primary brand
  primary: string;
  primaryForeground: string;
  
  // Secondary
  secondary: string;
  secondaryForeground: string;
  
  // Muted
  muted: string;
  mutedForeground: string;
  
  // Accent (used for scanning frame)
  accent: string;
  accentForeground: string;
  
  // Borders
  border: string;
  
  // Destructive / success
  destructive: string;
  success: string;
  
  // Communication Board rows (1-5)
  row1Bg: string;
  row2Bg: string;
  row3Bg: string;
  row4Bg: string;
  row5Bg: string;
  
  row1Text: string;
  row2Text: string;
  row3Text: string;
  row4Text: string;
  row5Text: string;
  
  row1Icon: string;
  row2Icon: string;
  row3Icon: string;
  row4Icon: string;
  row5Icon: string;

  row1Border: string;
  row2Border: string;
  row3Border: string;
  row4Border: string;
  row5Border: string;
  
  // Sub-board (inner pages) card overrides — per row
  row3SubBg: string;
  row3SubText: string;
  row3SubIcon: string;
  row3SubBorder: string;
  row5SubBg: string;
  row5SubText: string;
  row5SubIcon: string;
  row5SubBorder: string;
  
  // Scanning frame (global)
  scanFrameColor: string;
  scanFrameWidth: string; // e.g. "4"
  scanFrameGlow: string;  // e.g. "0" or "30"

  // Per-row scanning frame overrides
  row1ScanColor: string;
  row1ScanWidth: string;
  row1ScanGlow: string;
  row2ScanColor: string;
  row2ScanWidth: string;
  row2ScanGlow: string;
  row3ScanColor: string;
  row3ScanWidth: string;
  row3ScanGlow: string;
  row4ScanColor: string;
  row4ScanWidth: string;
  row4ScanGlow: string;
  row5ScanColor: string;
  row5ScanWidth: string;
  row5ScanGlow: string;
}

export interface DesignConfig {
  light: Partial<DesignTokenSet>;
  dark: Partial<DesignTokenSet>;
}

export interface PageDesignConfig {
  global: DesignConfig;
  [pageScope: string]: DesignConfig;
}

// Available page scopes
export const PAGE_SCOPES = [
  "global",
  "home",
  "communication",
  "keyboard",
  "entertainment",
  "settings",
  "games",
  "radio",
  "tv",
  "youtube",
] as const;

export type PageScope = typeof PAGE_SCOPES[number];

// Light mode defaults (matching current index.css)
export const LIGHT_DEFAULTS: DesignTokenSet = {
  background: "197 24% 95%",
  foreground: "195 100% 11%",
  card: "0 0% 100%",
  cardForeground: "195 100% 11%",
  primary: "184 65% 34%",
  primaryForeground: "0 0% 100%",
  secondary: "185 30% 90%",
  secondaryForeground: "195 100% 11%",
  muted: "185 20% 92%",
  mutedForeground: "195 30% 40%",
  accent: "20 95% 66%",
  accentForeground: "0 0% 100%",
  border: "185 30% 85%",
  destructive: "0 84% 70%",
  success: "142 76% 36%",
  row1Bg: "20 92% 90%",   // #FBDDCF
  row2Bg: "185 30% 75%",  // #B0D0D4
  row3Bg: "135 30% 87%",  // #D6E7DC
  row4Bg: "47 90% 90%",   // #FDF2CB
  row5Bg: "0 45% 84%",    // #F1BDBD
  row1Text: "195 100% 11%",
  row2Text: "195 100% 11%",
  row3Text: "195 100% 11%",
  row4Text: "195 100% 11%",
  row5Text: "195 100% 11%",
  row1Icon: "195 100% 11%",
  row2Icon: "195 100% 11%",
  row3Icon: "195 100% 11%",
  row4Icon: "195 100% 11%",
  row5Icon: "195 100% 11%",
  row1Border: "18 60% 78%",
  row2Border: "187 24% 64%",
  row3Border: "138 22% 74%",
  row4Border: "47 60% 76%",
  row5Border: "0 45% 72%",
  row3SubBg: "135 30% 87%",
  row3SubText: "195 100% 11%",
  row3SubIcon: "195 100% 11%",
  row3SubBorder: "138 22% 74%",
  row5SubBg: "0 45% 84%",
  row5SubText: "195 100% 11%",
  row5SubIcon: "195 100% 11%",
  row5SubBorder: "0 45% 72%",
  scanFrameColor: "20 95% 66%",
  scanFrameWidth: "4",
  scanFrameGlow: "0",
  row1ScanColor: "20 95% 66%",
  row1ScanWidth: "4",
  row1ScanGlow: "0",
  row2ScanColor: "20 95% 66%",
  row2ScanWidth: "4",
  row2ScanGlow: "0",
  row3ScanColor: "20 95% 66%",
  row3ScanWidth: "4",
  row3ScanGlow: "0",
  row4ScanColor: "20 95% 66%",
  row4ScanWidth: "4",
  row4ScanGlow: "0",
  row5ScanColor: "20 95% 66%",
  row5ScanWidth: "4",
  row5ScanGlow: "0",
};

// Dark mode defaults (matching current index.css)
export const DARK_DEFAULTS: DesignTokenSet = {
  background: "195 100% 11%",
  foreground: "185 30% 92%",
  card: "195 60% 20%",
  cardForeground: "185 30% 92%",
  primary: "184 65% 34%",
  primaryForeground: "0 0% 100%",
  secondary: "195 60% 18%",
  secondaryForeground: "185 30% 92%",
  muted: "195 50% 16%",
  mutedForeground: "185 25% 60%",
  accent: "20 95% 66%",
  accentForeground: "0 0% 100%",
  border: "195 50% 20%",
  destructive: "0 75% 65%",
  success: "142 60% 40%",
  row1Bg: "250 68% 59%",  // #5856D6
  row2Bg: "252 42% 48%",  // #5B45B1
  row3Bg: "326 92% 46%",  // #DD0D84
  row4Bg: "191 78% 40%",  // #1B9FB1
  row5Bg: "201 70% 30%",  // #156082
  row1Text: "0 0% 100%",
  row2Text: "0 0% 100%",
  row3Text: "0 0% 100%",
  row4Text: "0 0% 100%",
  row5Text: "0 0% 100%",
  row1Icon: "0 0% 100%",
  row2Icon: "0 0% 100%",
  row3Icon: "0 0% 100%",
  row4Icon: "0 0% 100%",
  row5Icon: "0 0% 100%",
  row1Border: "250 68% 69%",
  row2Border: "252 42% 58%",
  row3Border: "326 92% 56%",
  row4Border: "191 78% 50%",
  row5Border: "201 70% 40%",
  row3SubBg: "195 100% 11%",
  row3SubText: "0 0% 100%",
  row3SubIcon: "0 0% 100%",
  row3SubBorder: "326 92% 56%",
  row5SubBg: "195 100% 11%",
  row5SubText: "0 0% 100%",
  row5SubIcon: "0 0% 100%",
  row5SubBorder: "201 70% 40%",
  scanFrameColor: "20 95% 66%",
  scanFrameWidth: "4",
  scanFrameGlow: "30",
  row1ScanColor: "20 95% 66%",
  row1ScanWidth: "4",
  row1ScanGlow: "30",
  row2ScanColor: "20 95% 66%",
  row2ScanWidth: "4",
  row2ScanGlow: "30",
  row3ScanColor: "20 95% 66%",
  row3ScanWidth: "4",
  row3ScanGlow: "30",
  row4ScanColor: "20 95% 66%",
  row4ScanWidth: "4",
  row4ScanGlow: "30",
  row5ScanColor: "20 95% 66%",
  row5ScanWidth: "4",
  row5ScanGlow: "30",
};

// Token-to-CSS-variable mapping
export const TOKEN_TO_CSS: Record<string, string> = {
  background: "--background",
  foreground: "--foreground",
  card: "--card",
  cardForeground: "--card-foreground",
  primary: "--primary",
  primaryForeground: "--primary-foreground",
  secondary: "--secondary",
  secondaryForeground: "--secondary-foreground",
  muted: "--muted",
  mutedForeground: "--muted-foreground",
  accent: "--accent",
  accentForeground: "--accent-foreground",
  border: "--border",
  destructive: "--destructive",
  success: "--success",
  row1Bg: "--design-row1-bg",
  row2Bg: "--design-row2-bg",
  row3Bg: "--design-row3-bg",
  row4Bg: "--design-row4-bg",
  row5Bg: "--design-row5-bg",
  row1Text: "--design-row1-text",
  row2Text: "--design-row2-text",
  row3Text: "--design-row3-text",
  row4Text: "--design-row4-text",
  row5Text: "--design-row5-text",
  row1Icon: "--design-row1-icon",
  row2Icon: "--design-row2-icon",
  row3Icon: "--design-row3-icon",
  row4Icon: "--design-row4-icon",
  row5Icon: "--design-row5-icon",
  row1Border: "--design-row1-border",
  row2Border: "--design-row2-border",
  row3Border: "--design-row3-border",
  row4Border: "--design-row4-border",
  row5Border: "--design-row5-border",
  row3SubBg: "--design-row3-sub-bg",
  row3SubText: "--design-row3-sub-text",
  row3SubIcon: "--design-row3-sub-icon",
  row3SubBorder: "--design-row3-sub-border",
  row5SubBg: "--design-row5-sub-bg",
  row5SubText: "--design-row5-sub-text",
  row5SubIcon: "--design-row5-sub-icon",
  row5SubBorder: "--design-row5-sub-border",
  scanFrameColor: "--design-scan-color",
  scanFrameWidth: "--design-scan-width",
  scanFrameGlow: "--design-scan-glow",
  row1ScanColor: "--design-row1-scan-color",
  row1ScanWidth: "--design-row1-scan-width",
  row1ScanGlow: "--design-row1-scan-glow",
  row2ScanColor: "--design-row2-scan-color",
  row2ScanWidth: "--design-row2-scan-width",
  row2ScanGlow: "--design-row2-scan-glow",
  row3ScanColor: "--design-row3-scan-color",
  row3ScanWidth: "--design-row3-scan-width",
  row3ScanGlow: "--design-row3-scan-glow",
  row4ScanColor: "--design-row4-scan-color",
  row4ScanWidth: "--design-row4-scan-width",
  row4ScanGlow: "--design-row4-scan-glow",
  row5ScanColor: "--design-row5-scan-color",
  row5ScanWidth: "--design-row5-scan-width",
  row5ScanGlow: "--design-row5-scan-glow",
};

// Human-readable labels for the admin panel (Hebrew)
export const TOKEN_LABELS: Record<string, string> = {
  background: "רקע כללי",
  foreground: "טקסט כללי",
  card: "רקע כרטיס",
  cardForeground: "טקסט כרטיס",
  primary: "צבע ראשי",
  primaryForeground: "טקסט על צבע ראשי",
  secondary: "צבע משני",
  secondaryForeground: "טקסט על צבע משני",
  muted: "רקע מעומעם",
  mutedForeground: "טקסט מעומעם",
  accent: "צבע הדגשה",
  accentForeground: "טקסט על הדגשה",
  border: "גבול",
  destructive: "צבע מחיקה/שגיאה",
  success: "צבע הצלחה",
  row1Bg: "שורה 1 - רקע",
  row2Bg: "שורה 2 - רקע",
  row3Bg: "שורה 3 - רקע",
  row4Bg: "שורה 4 - רקע",
  row5Bg: "שורה 5 - רקע",
  row1Text: "שורה 1 - טקסט",
  row2Text: "שורה 2 - טקסט",
  row3Text: "שורה 3 - טקסט",
  row4Text: "שורה 4 - טקסט",
  row5Text: "שורה 5 - טקסט",
  row1Icon: "שורה 1 - אייקונים",
  row2Icon: "שורה 2 - אייקונים",
  row3Icon: "שורה 3 - אייקונים",
  row4Icon: "שורה 4 - אייקונים",
  row5Icon: "שורה 5 - אייקונים",
  row1Border: "שורה 1 - גבול",
  row2Border: "שורה 2 - גבול",
  row3Border: "שורה 3 - גבול",
  row4Border: "שורה 4 - גבול",
  row5Border: "שורה 5 - גבול",
  row3SubBg: "שורה 3 פנימי - רקע",
  row3SubText: "שורה 3 פנימי - טקסט",
  row3SubIcon: "שורה 3 פנימי - אייקונים",
  row3SubBorder: "שורה 3 פנימי - גבול",
  row5SubBg: "שורה 5 פנימי - רקע",
  row5SubText: "שורה 5 פנימי - טקסט",
  row5SubIcon: "שורה 5 פנימי - אייקונים",
  row5SubBorder: "שורה 5 פנימי - גבול",
  scanFrameColor: "מסגרת סריקה - צבע",
  scanFrameWidth: "מסגרת סריקה - עובי",
  scanFrameGlow: "מסגרת סריקה - זוהר",
  row1ScanColor: "שורה 1 סריקה - צבע",
  row1ScanWidth: "שורה 1 סריקה - עובי",
  row1ScanGlow: "שורה 1 סריקה - זוהר",
  row2ScanColor: "שורה 2 סריקה - צבע",
  row2ScanWidth: "שורה 2 סריקה - עובי",
  row2ScanGlow: "שורה 2 סריקה - זוהר",
  row3ScanColor: "שורה 3 סריקה - צבע",
  row3ScanWidth: "שורה 3 סריקה - עובי",
  row3ScanGlow: "שורה 3 סריקה - זוהר",
  row4ScanColor: "שורה 4 סריקה - צבע",
  row4ScanWidth: "שורה 4 סריקה - עובי",
  row4ScanGlow: "שורה 4 סריקה - זוהר",
  row5ScanColor: "שורה 5 סריקה - צבע",
  row5ScanWidth: "שורה 5 סריקה - עובי",
  row5ScanGlow: "שורה 5 סריקה - זוהר",
};

// Group tokens into categories for the admin panel
export const TOKEN_GROUPS = {
  "רקע ומשטחים": ["background", "card", "border", "muted"],
  "טקסט": ["foreground", "cardForeground", "mutedForeground"],
  "צבעי מותג": ["primary", "primaryForeground", "secondary", "secondaryForeground", "accent", "accentForeground"],
  "סטטוס": ["destructive", "success"],
  "שורות לוח תקשורת": [
    "row1Bg", "row1Text", "row1Icon", "row1Border",
    "row2Bg", "row2Text", "row2Icon", "row2Border",
    "row3Bg", "row3Text", "row3Icon", "row3Border",
    "row4Bg", "row4Text", "row4Icon", "row4Border",
    "row5Bg", "row5Text", "row5Icon", "row5Border",
  ],
  "כרטיסיות פנימיות - שורה 3": ["row3SubBg", "row3SubText", "row3SubIcon", "row3SubBorder"],
  "כרטיסיות פנימיות - שורה 5": ["row5SubBg", "row5SubText", "row5SubIcon", "row5SubBorder"],
  "מסגרת סריקה": ["scanFrameColor", "scanFrameWidth", "scanFrameGlow"],
  "מסגרת סריקה - שורה 1": ["row1ScanColor", "row1ScanWidth", "row1ScanGlow"],
  "מסגרת סריקה - שורה 2": ["row2ScanColor", "row2ScanWidth", "row2ScanGlow"],
  "מסגרת סריקה - שורה 3": ["row3ScanColor", "row3ScanWidth", "row3ScanGlow"],
  "מסגרת סריקה - שורה 4": ["row4ScanColor", "row4ScanWidth", "row4ScanGlow"],
  "מסגרת סריקה - שורה 5": ["row5ScanColor", "row5ScanWidth", "row5ScanGlow"],
};
